// Versão: 3.02
// Data..: 2026-09-26
// v3.02: título do contrato no cabeçalho (topo direito), corpo uniforme 10.5,
// ANEXO I em azul (sem roxo) e com mais espaçamento.
// v3.01: tipografia uniforme, espaçamento entre cláusulas, logo com proporção correta.
// Geração do contrato Makweb em PDF (client-side, com jsPDF), a partir de um orçamento.
// v3.00: usa o texto INTEGRAL da minuta (packages/shared/contractTemplate) — todas as
// cláusulas e subitens, sem omitir nada — logo Makweb no cabeçalho de cada página e
// tabela de SLA. Mesmo conteúdo do gerador do backend (fonte compartilhada).

import { jsPDF } from 'jspdf';
import {
  type Quote,
  type QuoteLineItem,
  CONTRACTED,
  SLA_DISCOUNTS,
  contractParties,
  contractClauses,
} from '@makweb/shared';

const BRAND: [number, number, number] = [0, 136, 204];
const DARK: [number, number, number] = [15, 23, 42];
const MUTED: [number, number, number] = [100, 116, 139];
const LIGHT: [number, number, number] = [241, 245, 249];

function money(v?: number): string {
  return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso?: string): string {
  if (!iso) return '____/____/______';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
}

async function loadLogo(): Promise<string | null> {
  try {
    const res = await fetch('/logo-light.png');
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateContractPdf(q: Quote): Promise<void> {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentW = pageW - margin * 2;
  const logo = await loadLogo();

  // Logo com proporção correta (evita achatamento). Calcula a altura a partir da
  // largura desejada e da razão real da imagem.
  let logoW = 110;
  let logoH = 30;
  if (logo) {
    try {
      const props = doc.getImageProperties(logo);
      logoH = (logoW / props.width) * props.height;
    } catch { /* usa o padrão */ }
  }

  const drawHeader = () => {
    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pageW, 4, 'F');
    if (logo) {
      try { doc.addImage(logo, 'PNG', margin, 18, logoW, logoH); } catch { /* ignora */ }
    } else {
      doc.setTextColor(...BRAND);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Makweb Soluções', margin, 34);
    }
    // Cabeçalho (topo direito): o nome/título do contrato (Título interno da proposta).
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(q.title || 'Contrato', pageW - margin, 30, { align: 'right' });
    doc.setDrawColor(...LIGHT);
    doc.line(margin, 54, pageW - margin, 54);
  };

  // Corpo padronizado: tamanho único (10.5) e entrelinha 1.5 para leitura confortável.
  const BODY_SIZE = 10.5;
  const LINE = 1.5;
  let y = 0;
  const startBody = () => { y = 72; };
  const newPage = () => { doc.addPage(); drawHeader(); startBody(); };
  const ensure = (needed: number) => { if (y + needed > pageH - margin) newPage(); };

  const paragraph = (text: string, opts: { bold?: boolean; size?: number; color?: [number, number, number]; gap?: number } = {}) => {
    const size = opts.size ?? BODY_SIZE;
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...(opts.color ?? DARK));
    const lines = doc.splitTextToSize(text, contentW) as string[];
    const lineH = size * LINE;
    ensure(lines.length * lineH + (opts.gap ?? 7));
    doc.text(lines, margin, y, { lineHeightFactor: LINE });
    y += lines.length * lineH + (opts.gap ?? 7);
  };

  const drawSlaTable = () => {
    const rowH = 18;
    const col1 = contentW * 0.6;
    ensure(rowH * (SLA_DISCOUNTS.length + 1) + 8);
    doc.setFillColor(...BRAND);
    doc.rect(margin, y, contentW, rowH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Tempo disponível', margin + 8, y + 12);
    doc.text('Desconto', margin + col1 + 8, y + 12);
    y += rowH;
    doc.setTextColor(...DARK);
    SLA_DISCOUNTS.forEach((row, i) => {
      if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(margin, y, contentW, rowH, 'F'); }
      doc.setFont('helvetica', 'normal');
      doc.text(row.range, margin + 8, y + 12);
      doc.setFont('helvetica', 'bold');
      doc.text(row.discount, margin + col1 + 8, y + 12);
      y += rowH;
    });
    y += 8;
  };

  drawHeader();
  startBody();

  // Título principal do corpo (o nome do contrato já aparece no cabeçalho).
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.setFontSize(18);
  ensure(30);
  doc.text('Proposta Técnica e Comercial', margin, y);
  y += 22;
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  doc.setFont('helvetica', 'normal');
  doc.text(q.title, margin, y);
  y += 22;

  // Preâmbulo — cada parte em um parágrafo separado, com respiro entre elas.
  paragraph('CONTRATO', { bold: true, size: 12, gap: 10 });
  paragraph('I – Configuram como partes:', { bold: true });
  const parties = contractParties(q);
  paragraph(parties.contratante, { gap: 10 });
  paragraph(parties.contratada, { gap: 10 });
  paragraph('A CONTRATANTE e a CONTRATADA têm entre si justo e contratado o seguinte:', { bold: true, gap: 12 });

  // Cláusulas (texto integral). Título com respiro maior antes (y += gap extra).
  for (const clause of contractClauses(q)) {
    ensure(40);
    y += 6; // espaço extra antes de cada cláusula
    paragraph(clause.title, { bold: true, size: 11.5, color: BRAND, gap: 9 });
    clause.paragraphs.forEach((p) => {
      paragraph(p);
      if (clause.slaTableAfterFirst && p.startsWith('2.5 ')) drawSlaTable();
    });
    if (clause.slaTableAfterFirst && !clause.paragraphs.some((p) => p.startsWith('2.5 '))) drawSlaTable();
  }

  // ---- ANEXO I ----
  newPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...BRAND);
  doc.text('ANEXO I — Contrato para Gestão em TI', margin, y);
  y += 26;
  paragraph(
    'Este documento é parte integrante do contrato de prestação de serviços entre a CONTRATANTE e a CONTRATADA, para descrever os valores acordados.',
    { color: MUTED, gap: 16 },
  );

  const drawItemsTable = (heading: string, items: QuoteLineItem[], total: number, totalLabel: string) => {
    ensure(74);
    y += 8; // respiro antes de cada seção de valores
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...BRAND);
    doc.text(heading, margin, (y += 14));
    y += 10;
    const cols = [
      { label: 'Descrição', w: contentW * 0.5, align: 'left' as const },
      { label: 'Unitário R$', w: contentW * 0.18, align: 'right' as const },
      { label: 'Qtd.', w: contentW * 0.1, align: 'right' as const },
      { label: 'Total R$', w: contentW * 0.22, align: 'right' as const },
    ];
    const rowH = 18;
    doc.setFillColor(...DARK);
    doc.rect(margin, y, contentW, rowH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    let x = margin;
    for (const col of cols) {
      const tx = col.align === 'right' ? x + col.w - 8 : x + 8;
      doc.text(col.label, tx, y + 12, { align: col.align });
      x += col.w;
    }
    y += rowH;
    doc.setFont('helvetica', 'normal');
    if (items.length === 0) {
      doc.setTextColor(...MUTED);
      doc.text('Nenhum item.', margin + 8, y + 12);
      y += rowH;
    } else {
      items.forEach((it, i) => {
        ensure(rowH);
        if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(margin, y, contentW, rowH, 'F'); }
        doc.setTextColor(...DARK);
        x = margin;
        const cells = [it.description || '—', money(it.unitPrice), String(it.quantity ?? ''), money(it.totalValue)];
        cells.forEach((cell, ci) => {
          const col = cols[ci];
          const tx = col.align === 'right' ? x + col.w - 8 : x + 8;
          const text = col.align === 'left' ? (doc.splitTextToSize(cell, col.w - 16) as string[])[0] : cell;
          doc.text(text, tx, y + 12, { align: col.align });
          x += col.w;
        });
        y += rowH;
      });
    }
    ensure(rowH);
    doc.setFillColor(...LIGHT);
    doc.rect(margin, y, contentW, rowH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK);
    doc.setFontSize(10);
    doc.text(totalLabel, margin + 8, y + 12);
    doc.text(money(total), margin + contentW - 8, y + 12, { align: 'right' });
    y += rowH + 12;
  };

  drawItemsTable('Adesão', q.setupItems ?? [], q.setupTotal ?? 0, 'TOTAL ADESÃO R$');
  drawItemsTable('Mensalidade', q.monthlyItems ?? [], q.monthlyTotal ?? 0, 'TOTAL MENSAL R$');

  // ---- Assinaturas ----
  ensure(140);
  y += 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  const city = (q.jurisdiction || 'Ribeirão Pires – SP').split('-')[0].trim();
  doc.text(`${city}, ${formatDate(q.adhesionDate)}.`, margin, (y += 16));
  y += 36;

  const clientSigner = q.signers?.[0];
  const drawSignature = (label: string, name: string, cpf: string, x: number) => {
    doc.setDrawColor(...MUTED);
    doc.line(x, y, x + 210, y);
    doc.setFontSize(9);
    doc.setTextColor(...BRAND);
    doc.setFont('helvetica', 'bold');
    doc.text(label, x, y + 14);
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'normal');
    doc.text(`Nome: ${name || '________________'}`, x, y + 28);
    doc.text(`CPF: ${cpf || '________________'}`, x, y + 40);
  };
  drawSignature('CONTRATADA', CONTRACTED.signerName, CONTRACTED.signerCpf, margin);
  drawSignature('CONTRATANTE', clientSigner?.name ?? q.clientName, clientSigner?.document ?? q.clientDocument ?? '', margin + contentW - 210);

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`${CONTRACTED.site} · Makweb Soluções — confidencial`, margin, pageH - 20);
    doc.text(`Página ${p} de ${pages}`, pageW - margin, pageH - 20, { align: 'right' });
  }

  const stamp = new Date().toISOString().slice(0, 10);
  const slug = (q.title || 'contrato').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  doc.save(`${slug || 'contrato'}-${stamp}.pdf`);
}
